// Phones get comfortable tiles and a minimap; anything wider fits the whole venue.
export const PHONE_MAX_WIDTH = 700;

export const COMFORTABLE_TILE_PX = 48;

export const COMPACT_TILE_PX = 44;

export const MIN_TILE_PX = 24;

export const MAX_TILE_PX = 56;

// Row labels, card and page padding, and the scrollbar the tiles never get.
export const MAP_CHROME_PX = 160;

// The gap between tiles; 33 columns of it is a tile's width many times over.
export const TILE_GAP_PX = 8;

export type MapFit = {
  readonly phone: boolean;
  readonly tilePx: number;
  readonly compact: boolean;
  readonly minimap: boolean;
};

// Desktop fits the whole venue between a floor and a ceiling; §15 wants both.
export function mapFit(viewportWidth: number, cols: number): MapFit {
  const columns = Math.max(1, cols);
  if (viewportWidth < PHONE_MAX_WIDTH) {
    const spread = columns * (COMFORTABLE_TILE_PX + TILE_GAP_PX);
    return {
      phone: true,
      tilePx: COMFORTABLE_TILE_PX,
      compact: false,
      minimap: spread > viewportWidth,
    };
  }
  // Tiles and the gaps between them share the room; the venue fits or says so.
  const room = viewportWidth - MAP_CHROME_PX;
  const each = Math.floor(room / columns) - TILE_GAP_PX;
  const tilePx = Math.max(MIN_TILE_PX, Math.min(MAX_TILE_PX, each));
  const spread = columns * (tilePx + TILE_GAP_PX);
  return {
    phone: false,
    tilePx,
    compact: tilePx < COMPACT_TILE_PX,
    minimap: spread > room,
  };
}

// The minimap is a scrollbar with a map on it; both directions are pure maths.
export function scrollTargetFor(
  fraction: number,
  scrollWidth: number,
  clientWidth: number,
): number {
  const clamped = Math.min(1, Math.max(0, fraction));
  return Math.max(0, Math.min(scrollWidth - clientWidth, clamped * scrollWidth - clientWidth / 2));
}

export type ViewportWindow = { readonly leftPct: number; readonly widthPct: number };

export function viewportWindow(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
): ViewportWindow {
  if (scrollWidth <= 0) return { leftPct: 0, widthPct: 100 };
  return {
    leftPct: (scrollLeft / scrollWidth) * 100,
    widthPct: Math.min(1, clientWidth / scrollWidth) * 100,
  };
}
